import { Box, Grid, Paper, Typography, Button } from "@mui/material";
import { Fragment, useMemo } from "react";
import { overwriteICD10TypeName } from "dto/encounter.dto";
import { useEncounter } from "pages/encounter/encounter.provider";
import CoderDiagnosisICDSection from "./coderDiagnosisICDSection";
import camelToCapitalizeText from "utils/camelToCapitalize";
import { useFieldArray } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";

interface CoderSectionProps {
    name: overwriteICD10TypeName;
}

const CoderDiagnosisSection = (props: CoderSectionProps) => {
    const { name } = props;

    const { encounterForm } = useEncounter();

    const {
        getValues: getCoderValue,
        setValue: setCoderValue,
        control
    } = encounterForm;
    const { fields, swap, remove, append } = useFieldArray({
        name,
        control
    });

    const swapSection = (
        from: overwriteICD10TypeName,
        fromIndex: number,
        to: overwriteICD10TypeName,
        position: "tail" | "head"
    ) => {
        const value = getCoderValue(`${from}.${fromIndex}`);
        if (to === "overwriteIcd10.principal") {
            const prevFrom = getCoderValue(from);
            const prevTo = getCoderValue(to);

            setCoderValue("overwriteIcd10.principal", [value]);
            setCoderValue(from, [prevFrom[0], ...prevTo, ...prevFrom.slice(1)]);
        } else {
            const prevTo = getCoderValue(to);
            if (position === "tail") {
                setCoderValue(to, [...prevTo, value]);
            } else {
                setCoderValue(to, [value, ...prevTo]);
            }
        }
    };

    const icdSwap = (from: number, to: number) => {
        const diagnosisOrder = [
            "overwriteIcd10.principal",
            "overwriteIcd10.comorbidity",
            "overwriteIcd10.complication",
            "overwriteIcd10.other",
            "overwriteIcd10.externalCause"
        ] as overwriteICD10TypeName[];

        const diagIndex = diagnosisOrder.findIndex((i) => i === name);
        if (from === 0 && to <= 0) {
            // swap to tail above
            swapSection(name, from, diagnosisOrder[diagIndex - 1], "tail");
            remove(from);
        } else if (from === fields.length - 1 && to >= fields.length) {
            // swap to head below
            swapSection(name, from, diagnosisOrder[diagIndex + 1], "head");
            remove(from);
        } else {
            swap(from, to);
        }
    };

    const title = useMemo(() => {
        const typeName = camelToCapitalizeText(
            name.replace("overwriteIcd10.", "")
        );

        if (["principal", "other"].includes(typeName.toLowerCase()))
            return `${typeName} Diagnosis`;

        return typeName;
    }, [name]);

    const handleOnAdd = () => {
        try {
            append({
                icd: [],
                diagnosis: []
            });
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = (index: number) => {
        try {
            remove(index);
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <Grid item container spacing={1}>
            <Grid item container>
                <Typography>
                    <b>{title}</b>
                </Typography>
            </Grid>
            <Grid item container>
                {fields.length <= 0 ? (
                    <Box
                        mb={1}
                        width="100%"
                        component={Paper}
                        variant="outlined"
                        textAlign="center"
                        py={2}
                    >
                        <Typography>No Diagnosis</Typography>
                    </Box>
                ) : null}
                {fields.map((f, nestedIndex) => {
                    return (
                        <Fragment key={`filed-${f.id}`}>
                            <CoderDiagnosisICDSection
                                nestedIndex={nestedIndex}
                                name={name}
                                swap={icdSwap}
                                handleDelete={handleDelete}
                            />
                        </Fragment>
                    );
                })}
                {name !== "overwriteIcd10.principal" && (
                    <Button
                        sx={{
                            borderRadius: 1,
                            border: "1px dashed var(--green, #05AA97)",
                            background: "rgba(5, 170, 151, 0.05)"
                        }}
                        onClick={() => {
                            handleOnAdd();
                        }}
                        variant="outlined"
                        fullWidth
                        startIcon={<AddIcon />}
                    >
                        <b>
                            Add More
                            {name === "overwriteIcd10.comorbidity"
                                ? " Comorbidity"
                                : name === "overwriteIcd10.complication"
                                  ? " Complication"
                                  : name === "overwriteIcd10.other"
                                    ? " Other"
                                    : name === "overwriteIcd10.externalCause"
                                      ? " External Cause"
                                      : ""}
                        </b>
                    </Button>
                )}
            </Grid>
        </Grid>
    );
};

export default CoderDiagnosisSection;
